import React, { useEffect } from 'react'
import { ViewStyle } from 'react-native'
import { useNavigation, NavigationProp } from '@react-navigation/native'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import I18n from 'APP/I18n'
import { USER_TRACKER_STATUSES } from 'APP/Lib/Enums'
import { SecondaryButton } from 'APP/Components/Buttons'
import { Metrics, Colors } from 'APP/Themes'
import {
  BannerContainer,
  CopyContainer,
  CopyStyles,
  CtaStyle,
  IconStyle,
  SyncCopyStyles,
  Title,
  TitleContainer,
} from './style'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
import Analytics from 'APP/Services/Analytics'
import SyncingLeftElement from './SyncingLeftElement'

interface StatusContent {
  copy: string | null
  shouldHaveCta: boolean
  title?: string | null
  titleIcon?: string
  LeftElement?: React.ComponentType
}

const STATUS_CONTENT_MAP: Record<string, StatusContent> = {
  [USER_TRACKER_STATUSES.NO_TRACKERS]: {
    title: I18n.t('ActiveMinutes.trackerBanner.noneConnected.title'),
    copy: I18n.t('ActiveMinutes.trackerBanner.noneConnected.copy'),
    shouldHaveCta: true,
  },
  [USER_TRACKER_STATUSES.NO_COMPATIBLE_TRACKERS]: {
    title: I18n.t('WellnessCenter.trackers.noneCompatible.title'),
    copy: I18n.t('WellnessCenter.trackers.noneCompatible.copy'),
    shouldHaveCta: true,
  },
  [USER_TRACKER_STATUSES.BROKEN_TRACKER]: {
    title: I18n.t('ActiveMinutes.trackerBanner.trackerBroken.title'),
    titleIcon: 'error',
    copy: I18n.t('ActiveMinutes.trackerBanner.trackerBroken.copy'),
    shouldHaveCta: true,
  },
  [USER_TRACKER_STATUSES.NO_DATA]: {
    title: I18n.t('ActiveMinutes.trackerBanner.noData.title'),
    copy: I18n.t('ActiveMinutes.trackerBanner.noData.copy'),
    shouldHaveCta: false,
  },
  [USER_TRACKER_STATUSES.SYNCING]: {
    copy: I18n.t('ActiveMinutes.trackerBanner.syncing.copy'),
    LeftElement: SyncingLeftElement,
    shouldHaveCta: false,
  },
}

interface TrackerStatusWrapperProps {
  status?: string
  children?: React.ReactNode
  requestedActivity?: string
  style?: ViewStyle
}

const TrackerStatusWrapper: React.FC<TrackerStatusWrapperProps> = ({
  status,
  children = null,
  requestedActivity,
  style,
}) => {
  const navigation = useNavigation<NavigationProp<Record<string, object | undefined>>>()
  if (!status || !Object.keys(STATUS_CONTENT_MAP).includes(status)) return children

  const setupTracker = () => {
    navigation.navigate('healthTrackerSettings', { requestedActivity })
  }

  const statusContent = STATUS_CONTENT_MAP[status as keyof typeof STATUS_CONTENT_MAP]
  if (!statusContent) return children

  const { copy, shouldHaveCta, title, titleIcon, LeftElement } = statusContent

  /* TODO: https://dialoguemd.atlassian.net/browse/DIA-83884 */
  /* eslint-disable-next-line react-hooks/rules-of-hooks */
  useEffect(() => {
    if (status && Object.keys(STATUS_CONTENT_MAP).includes(status)) {
      Analytics.trackEvent('banner_view', { banner_name: status })
    }
  }, [status])

  return (
    <BannerContainer style={style} testID={`${status}_BANNER`}>
      {title && (
        <TitleContainer>
          {titleIcon && (
            <MaterialIcon
              name={titleIcon}
              size={Metrics.icons.small}
              color={Colors.errorText}
              style={IconStyle}
            />
          )}
          <Title variant="h4">{title}</Title>
        </TitleContainer>
      )}
      {LeftElement ? (
        <CopyContainer>
          <LeftElement />
          <DialogueMarkdown markdown={copy} styles={SyncCopyStyles} containerStyles={{}} />
        </CopyContainer>
      ) : (
        <DialogueMarkdown markdown={copy} styles={CopyStyles} containerStyles={{}} />
      )}
      {shouldHaveCta && (
        <SecondaryButton style={CtaStyle} onPress={setupTracker}>
          {I18n.t('WellnessCenter.trackers.settings')}
        </SecondaryButton>
      )}
    </BannerContainer>
  )
}

export default TrackerStatusWrapper
